/**
 * Redux selectors for Contact Search
 */

import { createSelector } from 'reselect'
import { selectMembershipApiHost } from 'globalSelectors'

const getContactSearch = (state) => state.components?.body?.dataEntry?.contactSearch

export const selectContactSearchState = (state) => getContactSearch(state)

export const selectContactSearchResults = (state) => getContactSearch(state)?.results || []

export const selectContactSearchTotalCount = (state) => getContactSearch(state)?.totalCount || 0

export const selectContactSearchFirst = (state) => getContactSearch(state)?.first || 0

export const selectContactSearchRows = (state) => getContactSearch(state)?.rows || 50

export const selectContactSearchTotalPages = (state) => getContactSearch(state)?.totalPages || 0

export const selectContactSearchCurrentPage = (state) => getContactSearch(state)?.currentPage || 1

// Memoized to avoid creating new object on every call
let lastStatus = null
let lastStatusObject = { status: 'idle' }

export const selectContactSearchFetchStatus = (state) => {
    const currentStatus = getContactSearch(state)?.status || 'idle'
    if (currentStatus !== lastStatus) {
        lastStatus = currentStatus
        lastStatusObject = { status: currentStatus }
    }
    return lastStatusObject
}

export const selectContactSearchFilter = (state) => getContactSearch(state)?.filter || null

export const selectContactSearchSort = (state) => getContactSearch(state)?.sort || null

export const selectContactSearchTerm = (state) => getContactSearch(state)?.searchTerm ?? null

export const selectContactSearchUrl = createSelector(
    [selectMembershipApiHost],
    host => `${host}/contacts`
)

export const selectContactSearchFetchRequest = createSelector(
    [getContactSearch, selectContactSearchRows, selectContactSearchCurrentPage,
     selectContactSearchFilter, selectContactSearchSort, selectContactSearchUrl,
     selectContactSearchTerm],
    (store, count, page, filter, sort, url, searchTerm) => ({
        count, page, filter, sort, url, status: store?.status || 'idle', searchTerm
    })
)

// Contact Works Selectors - support multiple contacts by contactId
export const selectContactSearchWorksByContactId = (contactId) => createSelector(
    [getContactSearch],
    (contactSearch) => contactSearch?.works?.byContactId?.[contactId] || {
        status: 'idle',
        results: { total: 0, results: [] },
        currentPage: 1,
        totalPages: 0,
        row: 50,
        first: 0,
        sort: null,
        filter: null
    }
)

export const selectContactSearchWorksCurrentPage = (contactId) => createSelector(
    [selectContactSearchWorksByContactId(contactId)],
    (contactWorks) => contactWorks?.currentPage || 1
)

export const selectContactSearchWorksTotalPages = (contactId) => createSelector(
    [selectContactSearchWorksByContactId(contactId)],
    (contactWorks) => contactWorks?.totalPages || 0
)

export const selectContactSearchWorksRows = (contactId) => createSelector(
    [selectContactSearchWorksByContactId(contactId)],
    (contactWorks) => contactWorks?.row || 50
)

export const selectContactSearchWorksFirst = (contactId) => createSelector(
    [selectContactSearchWorksByContactId(contactId)],
    (contactWorks) => contactWorks?.first || 0
)

export const selectContactSearchWorksFilter = (contactId) => createSelector(
    [selectContactSearchWorksByContactId(contactId)],
    (contactWorks) => contactWorks?.filter || null
)

export const selectContactSearchWorksSort = (contactId) => createSelector(
    [selectContactSearchWorksByContactId(contactId)],
    (contactWorks) => contactWorks?.sort || null
)

export const selectContactSearchWorksFetchResults = (contactId) => createSelector(
    [selectContactSearchWorksByContactId(contactId)],
    (contactWorks) => contactWorks?.results || { total: 0, results: [] }
)

export const selectContactSearchWorksFetchStatus = (contactId) => createSelector(
    [selectContactSearchWorksByContactId(contactId)],
    (contactWorks) => contactWorks?.status || { status: 'idle' }
)

export const selectContactSearchWorksUrl = (state, contactId) => createSelector(
    [selectMembershipApiHost],
    host => `${host}/Contact/${contactId}/Works`
)(state)

// Helper function to build the works request object
const buildWorksRequest = (contactWorks, contactId, url) => ({
    currentContactId: contactId,
    count: contactWorks?.row || 50,
    page: contactWorks?.currentPage || 1,
    filter: contactWorks?.filter || null,
    sort: contactWorks?.sort || null,
    url,
    status: contactWorks?.status || { status: 'idle' }
})

// Main selector for works request
export const selectContactSearchWorksRequest = (contactId) => createSelector(
    [
        selectContactSearchWorksByContactId(contactId),
        (store) => selectContactSearchWorksUrl(store, contactId)
    ],
    (contactWorks, url) => buildWorksRequest(contactWorks, contactId, url)
)

// Add Potential Member Selectors
export const selectAddPotentialMember = (state) => getContactSearch(state)?.addPotentialMember || {
    firstName: '',
    middleName: '',
    lastName: '',
    remarks: '',
    errors: {},
    status: 'idle'
}

export const selectAddPotentialMemberErrors = (state) => getContactSearch(state)?.addPotentialMember?.errors || {}

export const selectAddPotentialMemberStatus = (state) => getContactSearch(state)?.addPotentialMember?.status || 'idle'
